if (!customElements.get('mobile-menu-v2')) {
  customElements.define('mobile-menu-v2', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('[role="dialog"]');
      this.opener = document.querySelector(`[aria-controls="${this.dialog.id}"]`);
      this.views = [...this.querySelectorAll('[data-mobile-view]')];
      this.currentView = this.views[0];
      this.viewAnimations = [];
      this.isOpen = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.locationToggle = this.querySelector('[data-mobile-menu-location]');
      this.locationPopover = this.querySelector('[data-mobile-menu-location-popover]');
      this.locationSelect = this.querySelector('[data-mobile-menu-location-select]');
      this.locationConfirm = this.querySelector('[data-mobile-menu-location-confirm]');
      this.locationState = this.querySelector('[data-mobile-menu-location-state]');
      this.populateLocationOptions();
      this.updateLocationState(localStorage.getItem('user_location'));
      this.opener?.addEventListener('click', event => {
        event.preventDefault();
        this.setOpen(!this.isOpen);
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-mobile-menu-location]')) {
          this.toggleLocationPopover();
          return;
        }
        if (event.target.closest('[data-mobile-menu-location-close]')) {
          this.toggleLocationPopover(false);
          return;
        }
        if (event.target.closest('[data-mobile-menu-location-confirm]')) {
          this.confirmLocation();
          return;
        }
        const screen = event.target.closest('[data-mobile-menu-screen]');
        if (screen) {
          this.returnFocus = screen;
          this.showView(screen.dataset.mobileMenuScreen);
        }
        if (event.target.closest('[data-mobile-menu-back]')) this.showView('main');
        if (event.target.closest('[data-mobile-menu-close]')) this.setOpen(false);
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        event.preventDefault();
        if (!this.locationPopover.hidden) this.toggleLocationPopover(false);
        else if (this.currentView.dataset.mobileView !== 'main') this.showView('main');
        else this.setOpen(false);
      }, options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.locationSelect?.addEventListener('change', () => {
        this.locationConfirm.disabled = !this.locationSelect.value;
      }, options);
      window.addEventListener('resize', () => {
        if (!this.opener?.getClientRects().length) this.setOpen(false, true);
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (event.target.contains(this) && this.opener?.getClientRects().length) this.setOpen(true);
      }, options);
      document.addEventListener('shopify:block:deselect', () => this.setOpen(false), options);
    }

    lockScroll() {
      if (this.savedOverflow) return;
      this.savedOverflow = [document.documentElement, document.body].map(element => ({
        element,
        value: element.style.getPropertyValue('overflow'),
        priority: element.style.getPropertyPriority('overflow')
      }));
      this.savedOverflow.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
    }

    unlockScroll() {
      this.savedOverflow?.forEach(({ element, value, priority }) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.savedOverflow = null;
    }

    setOpen(open, immediate = false) {
      if (open === this.isOpen && !immediate) return;
      if (!open && this.dialog.hidden) {
        document.body.classList.remove('mobile-menu-v2-open');
        return;
      }
      const from = this.dialog.hidden ? 'translateY(-100%)' : getComputedStyle(this.dialog).transform;
      this.animation?.cancel();
      this.animation = null;
      this.isOpen = open;
      this.opener?.setAttribute('aria-expanded', String(open));
      if (open) {
        document.body.classList.add('mobile-menu-v2-open');
        if (this.dialog.hidden) {
          this.resetViews();
          this.updateLocationState(localStorage.getItem('user_location'));
          this.lockScroll();
          this.dialog.hidden = false;
          this.dialog.setAttribute('aria-hidden', 'false');
          this.dialog.querySelector('[data-mobile-menu-close]')?.focus({ preventScroll: true });
        }
      }
      const finish = () => {
        if (!this.isOpen) {
          this.toggleLocationPopover(false);
          this.dialog.hidden = true;
          this.dialog.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('mobile-menu-v2-open');
          this.unlockScroll();
          this.resetViews();
          if (this.isConnected && this.opener?.getClientRects().length) this.opener.focus({ preventScroll: true });
        }
      };
      if (immediate || this.motion.matches) {
        finish();
        return;
      }
      const animation = this.dialog.animate([
        { transform: from },
        { transform: open ? 'translateY(0)' : 'translateY(-100%)' }
      ], { duration: 550, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both' });
      this.animation = animation;
      animation.finished.then(() => {
        if (this.animation !== animation) return;
        finish();
        animation.cancel();
        this.animation = null;
      }, () => {});
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(element => !element.closest('[hidden], [inert]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    populateLocationOptions() {
      if (!this.locationSelect) return;
      const states = [...document.querySelectorAll('.state-dropdown [data-country-code]')];
      states.forEach(state => {
        const option = document.createElement('option');
        option.value = state.dataset.countryCode;
        option.textContent = state.textContent.trim();
        this.locationSelect.append(option);
      });
    }

    toggleLocationPopover(open = this.locationPopover.hidden) {
      if (!this.locationPopover) return;
      this.locationPopover.hidden = !open;
      this.locationToggle?.setAttribute('aria-expanded', String(open));
      if (open) this.locationSelect?.focus({ preventScroll: true });
    }

    updateLocationState(state) {
      const code = String(state || '').toUpperCase();
      if (this.locationState) this.locationState.textContent = code;
      this.locationToggle?.setAttribute('aria-label', code ? `Delivery state: ${code}` : 'Select delivery state');
      if (this.locationSelect) this.locationSelect.value = code;
      if (this.locationConfirm) this.locationConfirm.disabled = !code;
    }

    confirmLocation() {
      const code = this.locationSelect?.value;
      if (!code) return;
      localStorage.setItem('user_location', code);
      document.querySelectorAll('[data-default-option]').forEach(element => element.classList.add('d-none'));
      document.querySelectorAll('[data-selected-option]').forEach(element => element.classList.remove('d-none'));
      document.querySelectorAll('[data-selected-option] .state').forEach(element => {
        element.textContent = code;
      });
      this.updateLocationState(code);
      this.toggleLocationPopover(false);
      window.manage_california_products?.();
    }

    resetViews() {
      this.viewAnimations.forEach(animation => animation.cancel());
      this.viewAnimations = [];
      this.views.forEach((view, index) => {
        view.hidden = index !== 0;
        view.inert = index !== 0;
        view.scrollTop = 0;
      });
      this.currentView = this.views[0];
      this.toggleLocationPopover(false);
    }

    showView(name) {
      const next = this.views.find(view => view.dataset.mobileView === name);
      if (!next || next === this.currentView) return;
      this.viewAnimations.forEach(animation => animation.cancel());
      const previous = this.currentView;
      this.views.forEach(view => {
        view.hidden = view !== previous && view !== next;
        view.inert = view !== next;
      });
      this.currentView = next;
      next.scrollTop = 0;
      const focusTarget = name === 'main' ? this.returnFocus : next.querySelector('button, a');
      focusTarget?.focus({ preventScroll: true });
      if (this.motion.matches) {
        previous.hidden = true;
        return;
      }
      const direction = name === 'main' ? -1 : 1;
      const options = { duration: 350, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both' };
      const outgoing = previous.animate([
        { transform: 'translateX(0)' }, { transform: `translateX(${-direction * 100}%)` }
      ], options);
      const incoming = next.animate([
        { transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }
      ], options);
      this.viewAnimations = [outgoing, incoming];
      incoming.finished.then(() => {
        if (this.currentView !== next || !this.viewAnimations.includes(incoming)) return;
        previous.hidden = true;
        this.viewAnimations.forEach(animation => animation.cancel());
        this.viewAnimations = [];
      }, () => {});
    }

    disconnectedCallback() {
      this.animation?.cancel();
      this.viewAnimations.forEach(animation => animation.cancel());
      this.dialog.hidden = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mobile-menu-v2-open');
      this.unlockScroll();
      this.events.abort();
    }
  });
}
